import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { Save, Eye, Edit, Plus, Trash2, Loader2 } from 'lucide-react';
import { landingPageAPI } from '../../lib/api';

const LandingPageEditor = () => {
  const [activeTab, setActiveTab] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({
    hero: {
      badge: '',
      title: '',
      subtitle: '',
      primaryButton: '',
      secondaryButton: ''
    },
    features: [],
    pricing: [],
    testimonials: [],
    faqs: []
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await landingPageAPI.get();
      const data = response.data;
      
      // Ensure all arrays exist and have stable UIDs
      const ensureUids = (arr) => (arr || []).map(item => ({ _uid: item._uid || crypto.randomUUID(), ...item }));
      setContent({
        hero: data.hero || {
          badge: '',
          title: '',
          subtitle: '',
          primaryButton: '',
          secondaryButton: ''
        },
        features: ensureUids(data.features),
        pricing: ensureUids(data.pricing),
        testimonials: ensureUids(data.testimonials),
        faqs: ensureUids(data.faqs)
      });
    } catch (error) {
      console.error('Failed to fetch landing page content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await landingPageAPI.update(content);
      toast.success('Landing page content saved successfully!');
    } catch (error) {
      console.error('Failed to save landing page content:', error);
      toast.error('Failed to save content. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    window.open('/', '_blank');
  };

  // Hero Section Handlers
  const updateHero = (field, value) => {
    setContent(prev => ({
      ...prev,
      hero: { ...prev.hero, [field]: value }
    }));
  };

  // Features Handlers
  const addFeature = () => {
    setContent(prev => ({
      ...prev,
      features: [...prev.features, { _uid: crypto.randomUUID(), title: '', description: '', icon: 'ClipboardList' }]
    }));
  };

  const updateFeature = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      features: prev.features.map((f, i) => i === index ? { ...f, [field]: value } : f)
    }));
  };

  const deleteFeature = (index) => {
    setContent(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  // Pricing Handlers
  const addPricingPlan = () => {
    setContent(prev => ({
      ...prev,
      pricing: [...prev.pricing, {
        _uid: crypto.randomUUID(),
        name: '',
        price: '$0',
        period: 'per month',
        description: '',
        features: [],
        cta: 'Get Started',
        highlighted: false
      }]
    }));
  };

  const updatePricing = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      pricing: prev.pricing.map((p, i) => i === index ? { ...p, [field]: value } : p)
    }));
  };

  const deletePricing = (index) => {
    setContent(prev => ({
      ...prev,
      pricing: prev.pricing.filter((_, i) => i !== index)
    }));
  };

  const addPricingFeature = (planIndex) => {
    setContent(prev => ({
      ...prev,
      pricing: prev.pricing.map((p, i) => 
        i === planIndex ? { ...p, features: [...p.features, ''] } : p
      )
    }));
  };

  const updatePricingFeature = (planIndex, featureIndex, value) => {
    setContent(prev => ({
      ...prev,
      pricing: prev.pricing.map((p, i) => 
        i === planIndex ? {
          ...p,
          features: p.features.map((f, fi) => fi === featureIndex ? value : f)
        } : p
      )
    }));
  };

  const deletePricingFeature = (planIndex, featureIndex) => {
    setContent(prev => ({
      ...prev,
      pricing: prev.pricing.map((p, i) => 
        i === planIndex ? {
          ...p,
          features: p.features.filter((_, fi) => fi !== featureIndex)
        } : p
      )
    }));
  };

  // Testimonials Handlers
  const addTestimonial = () => {
    setContent(prev => ({
      ...prev,
      testimonials: [...prev.testimonials, {
        _uid: crypto.randomUUID(),
        name: '',
        role: '',
        company: '',
        content: '',
        rating: 5
      }]
    }));
  };

  const updateTestimonial = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      testimonials: prev.testimonials.map((t, i) => i === index ? { ...t, [field]: value } : t)
    }));
  };

  const deleteTestimonial = (index) => {
    setContent(prev => ({
      ...prev,
      testimonials: prev.testimonials.filter((_, i) => i !== index)
    }));
  };

  // FAQs Handlers
  const addFaq = () => {
    setContent(prev => ({
      ...prev,
      faqs: [...prev.faqs, { _uid: crypto.randomUUID(), question: '', answer: '' }]
    }));
  };

  const updateFaq = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      faqs: prev.faqs.map((f, i) => i === index ? { ...f, [field]: value } : f)
    }));
  };

  const deleteFaq = (index) => {
    setContent(prev => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
              Landing Page Editor
            </h1>
            <p className="text-slate-400 mt-1">Customize your entire marketing website</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handlePreview}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Preview
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="bg-amber-500/10 border border-amber-500/40 text-amber-100 rounded p-4 text-sm leading-relaxed">
          The saved record in this editor replaces the code defaults on the public homepage.
          Set prices to Free $0 (3 users, 100 work orders total), Pro $29 per month flat for the company, and Enterprise to the words Contact us.
          Delete testimonials, customer counts, downtime percentages, star ratings, and certification claims.
          Sales and support email is bc2801@hotmail.com. Privacy, Terms, and Cookies are draft pages at /privacy, /terms, and /cookies.
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-700 overflow-x-auto">
          {['hero', 'features', 'pricing', 'testimonials', 'faqs'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 font-medium uppercase text-sm tracking-wide transition ${
                activeTab === tab
                  ? 'border-b-2 border-amber-500 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Hero Section Editor */}
        {activeTab === 'hero' && (
          <Card className="bg-slate-900 border-slate-700 p-6">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Edit className="w-6 h-6" />
              Hero Section
            </h2>
            <div className="space-y-6">
              <div>
                <Label className="text-slate-400 mb-2">Badge Text</Label>
                <Input
                  value={content.hero.badge}
                  onChange={(e) => updateHero('badge', e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="Work orders, preventive maintenance, and inventory"
                />
              </div>
              <div>
                <Label className="text-slate-400 mb-2">Main Headline</Label>
                <Input
                  value={content.hero.title}
                  onChange={(e) => updateHero('title', e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white text-lg"
                  placeholder="Your main value proposition"
                />
              </div>
              <div>
                <Label className="text-slate-400 mb-2">Subheadline</Label>
                <Textarea
                  value={content.hero.subtitle}
                  onChange={(e) => updateHero('subtitle', e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white"
                  rows={3}
                  placeholder="Supporting text that explains your product"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400 mb-2">Primary Button Text</Label>
                  <Input
                    value={content.hero.primaryButton}
                    onChange={(e) => updateHero('primaryButton', e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 mb-2">Secondary Button Text</Label>
                  <Input
                    value={content.hero.secondaryButton}
                    onChange={(e) => updateHero('secondaryButton', e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Features Section Editor */}
        {activeTab === 'features' && (
          <Card className="bg-slate-900 border-slate-700 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Edit className="w-6 h-6" />
                Features Section
              </h2>
              <Button onClick={addFeature} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Feature
              </Button>
            </div>
            <div className="space-y-6">
              {content.features.map((feature, index) => (
                <Card key={feature._uid} className="bg-slate-800 border-slate-700 p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-white font-semibold">Feature {index + 1}</h3>
                    <Button
                      onClick={() => deleteFeature(index)}
                      variant="ghost"
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-400 mb-2">Title</Label>
                      <Input
                        value={feature.title}
                        onChange={(e) => updateFeature(index, 'title', e.target.value)}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 mb-2">Description</Label>
                      <Textarea
                        value={feature.description}
                        onChange={(e) => updateFeature(index, 'description', e.target.value)}
                        className="bg-slate-700 border-slate-600 text-white"
                        rows={2}
                      />
                    </div>
                  </div>
                </Card>
              ))}
              {content.features.length === 0 && (
                <p className="text-slate-500 text-center py-8">No features added yet. Click "Add Feature" to get started.</p>
              )}
            </div>
          </Card>
        )}

        {/* Pricing Section Editor */}
        {activeTab === 'pricing' && (
          <Card className="bg-slate-900 border-slate-700 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Edit className="w-6 h-6" />
                Pricing Plans
              </h2>
              <Button onClick={addPricingPlan} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Plan
              </Button>
            </div>
            <div className="space-y-6">
              {content.pricing.map((plan, planIndex) => (
                <Card key={plan._uid} className="bg-slate-800 border-slate-700 p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-white font-semibold">{plan.name || `Plan ${planIndex + 1}`}</h3>
                    <Button
                      onClick={() => deletePricing(planIndex)}
                      variant="ghost"
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-400 mb-2">Plan Name</Label>
                        <Input
                          value={plan.name}
                          onChange={(e) => updatePricing(planIndex, 'name', e.target.value)}
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-400 mb-2">Price</Label>
                        <Input
                          value={plan.price}
                          onChange={(e) => updatePricing(planIndex, 'price', e.target.value)}
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-400 mb-2">Period</Label>
                        <Input
                          value={plan.period}
                          onChange={(e) => updatePricing(planIndex, 'period', e.target.value)}
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-400 mb-2">Call to Action</Label>
                        <Input
                          value={plan.cta}
                          onChange={(e) => updatePricing(planIndex, 'cta', e.target.value)}
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-400 mb-2">Description</Label>
                      <Input
                        value={plan.description}
                        onChange={(e) => updatePricing(planIndex, 'description', e.target.value)}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <Label className="text-slate-400">Features</Label>
                        <Button
                          onClick={() => addPricingFeature(planIndex)}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 h-7"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {plan.features && plan.features.map((feature, featureIndex) => (
                          <div key={`${plan._uid}-feature-${featureIndex}`} className="flex gap-2">
                            <Input
                              value={feature}
                              onChange={(e) => updatePricingFeature(planIndex, featureIndex, e.target.value)}
                              className="bg-slate-700 border-slate-600 text-white flex-1"
                              placeholder="Feature description"
                            />
                            <Button
                              onClick={() => deletePricingFeature(planIndex, featureIndex)}
                              size="sm"
                              variant="ghost"
                              className="text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={plan.highlighted}
                        onChange={(e) => updatePricing(planIndex, 'highlighted', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <Label className="text-slate-400">Highlight this plan</Label>
                    </div>
                  </div>
                </Card>
              ))}
              {content.pricing.length === 0 && (
                <p className="text-slate-500 text-center py-8">No pricing plans added yet.</p>
              )}
            </div>
          </Card>
        )}

        {/* Testimonials Section Editor */}
        {activeTab === 'testimonials' && (
          <Card className="bg-slate-900 border-slate-700 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Edit className="w-6 h-6" />
                Testimonials
              </h2>
              <Button onClick={addTestimonial} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Testimonial
              </Button>
            </div>
            <div className="space-y-6">
              {content.testimonials.map((testimonial, index) => (
                <Card key={testimonial._uid} className="bg-slate-800 border-slate-700 p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-white font-semibold">Testimonial {index + 1}</h3>
                    <Button
                      onClick={() => deleteTestimonial(index)}
                      variant="ghost"
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-slate-400 mb-2">Name</Label>
                        <Input
                          value={testimonial.name}
                          onChange={(e) => updateTestimonial(index, 'name', e.target.value)}
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-400 mb-2">Role</Label>
                        <Input
                          value={testimonial.role}
                          onChange={(e) => updateTestimonial(index, 'role', e.target.value)}
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-400 mb-2">Company</Label>
                        <Input
                          value={testimonial.company}
                          onChange={(e) => updateTestimonial(index, 'company', e.target.value)}
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-400 mb-2">Testimonial Content</Label>
                      <Textarea
                        value={testimonial.content}
                        onChange={(e) => updateTestimonial(index, 'content', e.target.value)}
                        className="bg-slate-700 border-slate-600 text-white"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 mb-2">Rating (1-5)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="5"
                        value={testimonial.rating}
                        onChange={(e) => updateTestimonial(index, 'rating', parseInt(e.target.value))}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                </Card>
              ))}
              {content.testimonials.length === 0 && (
                <p className="text-slate-500 text-center py-8">No testimonials added yet.</p>
              )}
            </div>
          </Card>
        )}

        {/* FAQs Section Editor */}
        {activeTab === 'faqs' && (
          <Card className="bg-slate-900 border-slate-700 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Edit className="w-6 h-6" />
                FAQs
              </h2>
              <Button onClick={addFaq} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Add FAQ
              </Button>
            </div>
            <div className="space-y-6">
              {content.faqs.map((faq, index) => (
                <Card key={faq._uid} className="bg-slate-800 border-slate-700 p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-white font-semibold">FAQ {index + 1}</h3>
                    <Button
                      onClick={() => deleteFaq(index)}
                      variant="ghost"
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-400 mb-2">Question</Label>
                      <Input
                        value={faq.question}
                        onChange={(e) => updateFaq(index, 'question', e.target.value)}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 mb-2">Answer</Label>
                      <Textarea
                        value={faq.answer}
                        onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                        className="bg-slate-700 border-slate-600 text-white"
                        rows={3}
                      />
                    </div>
                  </div>
                </Card>
              ))}
              {content.faqs.length === 0 && (
                <p className="text-slate-500 text-center py-8">No FAQs added yet.</p>
              )}
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default LandingPageEditor;
